import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import {
    ConfirmActionV2DialogComponent,
    ConfirmActionV2DialogInput,
    ConfirmActionV2DialogResult,
} from '../../components/v2-layout/shared/dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { buildPrefillNoticeContent, PREFILL_NOTICE_COPY } from '../../components/v2-layout/stripe-treasury/stripe-treasury.constants';
import { MatDialogSizes } from '../../enums/mat-dialog-sizes.enum';
import { StripeConnectService } from './stripe-connect.service';

/**
 * SD-9498: the pre-verification notice, shared by both KYC seams.
 *
 * The v2 dashboard and the v1 `FinancialConnectionsService` ask the same question at the same point
 * in the flow and act on the same three-way answer; only their terminal actions and teardown
 * operator differ. Keeping the preflight, the dialog configuration and the result semantics here
 * means a change to any of them lands once — the first version duplicated all three across the two
 * seams, and duplicated two defects along with them.
 */
@Injectable({
    providedIn: 'root',
})
export class PrefillNoticeService {
    constructor(private _stripeConnectService: StripeConnectService) {}

    /**
     * Ask what PayHOA cannot send, and show the admin a notice when there is something to say.
     *
     * Emits `{success: true}` — proceed — when the org's details are all usable, and also whenever
     * anything about the check goes wrong. That fall-through is the point: this is an advisory, and
     * an advisory that blocks verification is worse than the bug it softens. A failed request, a
     * 200 whose shape we did not expect, a throw anywhere in the pipe — all resolve to "carry on",
     * so the caller's terminal path always runs and the caller's in-flight flag always clears.
     *
     * @param dialog the seam's own dialog service — v2 injects `MatDialog`, v1 the MDC instance it
     *               already keeps for the 2FA gate, since the notice is a v2 component
     */
    public confirm(organizationId: number, dialog: MatDialog): Observable<ConfirmActionV2DialogResult> {
        return this._stripeConnectService.getPrefillCheck(organizationId).pipe(
            // `?? []` rather than a bare read: a 200 without the key is a real deploy-window shape
            // in this codebase, and dereferencing it would throw inside the pipe.
            map((response) => buildPrefillNoticeContent(response?.omissions ?? [])),
            switchMap((content) => {
                if (!content) {
                    return of<ConfirmActionV2DialogResult>({ success: true });
                }

                const data: ConfirmActionV2DialogInput = {
                    title: PREFILL_NOTICE_COPY.title,
                    content,
                    confirmButton: PREFILL_NOTICE_COPY.confirmButton,
                    secondaryButton: PREFILL_NOTICE_COPY.secondaryButton,
                };

                return dialog.open(ConfirmActionV2DialogComponent, {
                    panelClass: ['v2-dialog', 'confirmation-layout'],
                    // MD, not the confirm-dialog default SM: three buttons (Cancel / Review
                    // organization settings / Continue to verification) overflow 555px and clip.
                    width: MatDialogSizes.MD,
                    autoFocus: 'dialog',
                    data,
                }).afterClosed();
            }),
            // Last, deliberately: placed before switchMap it would only cover a failed request,
            // leaving a throw inside the pipe to reach an error-less subscribe — which on the v1
            // seam never calls onComplete(), latching the caller's button dead until a reload.
            catchError(() => of<ConfirmActionV2DialogResult>({ success: true })),
        );
    }
}
